import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { numberLocale } from '@/lib/language';
import { WORKBENCH_STATUSES, type WorkbenchStatus } from '@/hooks/workbench/statusScope';
import type { StatusCounts } from '@/hooks/useWorkbenchStatusCounts';
import { useEntwurf } from '@/entwurf/EntwurfOptionen';

interface WorkbenchStatusTabsProps {
  active: WorkbenchStatus;
  /** `null` heisst ausdruecklich "unbekannt", nicht "null Produkte". */
  counts: StatusCounts | null;
  onChange: (status: WorkbenchStatus) => void;
}

/**
 * Die drei Statusreiter der Werkbank. Reine Praesentation: der Reiter bestimmt
 * die MENGE, die Filterleiste darunter verfeinert sie.
 *
 * Zwei Entscheidungen, die nicht offensichtlich sind:
 *
 * 1. Ein Klick auf den bereits aktiven Reiter meldet NICHTS. Der Wechsel setzt
 *    beim Aufrufer den Puffer und den Cursor zurueck; ein versehentlicher
 *    Zweitklick wuerde sonst eine begonnene Sichtung verwerfen.
 *
 * 2. Ohne Zahlen wird gar keine Zahl gerendert, auch keine 0. Eine 0 waere eine
 *    Behauptung ueber den Feed ("hier ist nichts"), und solange die Zaehlung
 *    laeuft oder fehlgeschlagen ist, gibt es diese Aussage nicht.
 *
 * Tastatur nach Radix-Tabs-Konvention: die Leiste ist EIN Tabstopp, innerhalb
 * bewegen Pfeiltasten, Home und End springen an die Raender. Am Rand wird
 * bewusst NICHT umgebrochen; ein stiller Sprung von "Abgelehnt" zurueck auf
 * "Offen" liest sich wie ein Fehlklick.
 */
export function WorkbenchStatusTabs({ active, counts, onChange }: WorkbenchStatusTabsProps) {
  const { t, i18n } = useTranslation('workbench');
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  /* Rein visuell. Ausserhalb der Entwurfsseite liefert der Haken
     'unterstrich', also den heutigen Zustand. Die Tastatur- und
     ARIA-Verdrahtung darunter ist in beiden Faellen dieselbe. */
  const { reiterStil } = useEntwurf();
  const segmente = reiterStil === 'segmente';

  const select = (next: WorkbenchStatus) => {
    if (next === active) return;
    onChange(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const index = WORKBENCH_STATUSES.indexOf(active);
    let target: number | null = null;

    if (event.key === 'ArrowRight') target = index + 1;
    else if (event.key === 'ArrowLeft') target = index - 1;
    else if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = WORKBENCH_STATUSES.length - 1;
    else return;

    event.preventDefault();
    if (target < 0 || target >= WORKBENCH_STATUSES.length) return;
    // V3-Befund: der Roving Tabindex war nur halb gebaut — tabIndex wanderte,
    // der Fokus nicht. Nach einem Pfeildruck sass der Fokusring auf einem
    // Knopf, der im selben Render auf tabIndex -1 gefallen war: Fokus und
    // Auswahl zeigten auf verschiedene Reiter, und der naechste Tab-Druck
    // verliess die Leiste von der falschen Stelle aus.
    tabRefs.current[target]?.focus();
    select(WORKBENCH_STATUSES[target]);
  };

  return (
    <div
      role="tablist"
      aria-label={t('statusTabs.label')}
      onKeyDown={handleKeyDown}
      /* V3-Befund: die drei Reiter brauchen mit Zahlen rund 435 px, auf 390 px
         hat die Content-Box 358 — ohne eigenen Scroll schnitt `main` mit
         overflow-x-hidden den Reiter "Abgelehnt" schlicht ab. Scrollbare
         Reiter sind das etablierte Muster; kein Umbruch, damit die Leiste ein
         einzeiliges Tablist bleibt. */
      className={cn(
        'flex items-center overflow-x-auto',
        segmente
          /* Hausmuster aus `ui/tabs.tsx`, das Einstellungen und Roadmap schon
             benutzen. Kein `border-b`: der Unterstrich braucht eine
             Grundlinie ueber die volle Breite, und in der einzeiligen
             Kopfzone endet sie mitten in der Zeile. */
          ? 'h-10 flex-shrink-0 gap-0.5 rounded-md bg-muted p-1'
          : 'gap-1 border-b border-border',
      )}
    >
      {WORKBENCH_STATUSES.map((status, tabPosition) => {
        const isActive = status === active;
        const count = counts?.[status];
        const label = t(`statusTabs.${status}`);

        return (
          <button
            key={status}
            ref={(el) => {
              tabRefs.current[tabPosition] = el;
            }}
            type="button"
            role="tab"
            /* V3-Befund: ein Tablist ohne Tabpanel ist unvollstaendig — der
               Screenreader kann nicht sagen, WAS der Reiter steuert. Die
               Gegenstuecke setzt WorkbenchReviewer (id "workbench-tabpanel"
               + aria-labelledby auf den aktiven Reiter). */
            id={`workbench-tab-${status}`}
            aria-controls="workbench-tabpanel"
            aria-selected={isActive}
            // Nur der aktive Reiter ist ein Tabstopp; innerhalb bewegen Pfeile.
            tabIndex={isActive ? 0 : -1}
            onClick={() => select(status)}
            className={cn(
              'flex flex-shrink-0 items-center gap-1.5 text-sm font-medium transition-all',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              segmente
                ? cn(
                    'rounded-sm px-3 py-1.5',
                    isActive
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-foreground/70 hover:text-foreground',
                  )
                : cn(
                    'gap-2 border-b-2 px-3 py-2',
                    isActive
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  ),
            )}
          >
            <span>{label}</span>
            {count !== undefined && (
              <span
                /* V3-Befund: beide Abzeichen rissen AA (aktiv 3.90:1 hell,
                   inaktiv 4.29 hell / 3.78 dunkel — selbst nachgerechnet).
                   Der Text steht jetzt in beiden auf foreground (15.6 bis
                   16.3); Aktiv/Inaktiv unterscheidet weiter die Chipflaeche
                   und die Reiterunterstreichung, nicht die Textfarbe. */
                className={cn(
                  'text-xs tabular-nums',
                  segmente
                    /* Keine Flaeche in der Flaeche: das Segment ist selbst
                       schon eine Kachel, ein Abzeichen darin waere die
                       dritte Ebene auf engstem Raum. Die Zahl steht deshalb
                       nur als Text, gedaempft gegen die Beschriftung. */
                    /* Beide auf /70. Mit /60 mass die inaktive Zahl im hellen
                       Thema 4.56:1 - sie besteht AA, aber so knapp wie die
                       muted-foreground-Faelle, die derselbe Durchgang bereits
                       anheben musste. Die Daempfung gegen die Beschriftung
                       leistet ohnehin die Schriftgroesse (12 gegen 14 px),
                       nicht die Farbe. Mit /70 sind es 6.36:1. */
                    ? 'font-normal text-foreground/70'
                    : cn(
                        'rounded-full px-2 py-0.5 text-foreground',
                        isActive ? 'bg-primary/10' : 'bg-muted',
                      ),
                )}
              >
                {count.toLocaleString(numberLocale(i18n.language))}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
